import { IcWhatsapp } from "@racoon/icons";
import { Namespace, useT } from "@racoon/i18n";
import { reformatPhoneNumber } from "@racoon/lib";
import { cn } from "@racoon/ui";
import type { OrderRow } from "../model/adapters";
import { getAuthorBadgeClass } from "~/shared/lib/author-badge";
import { getAuthorInitials, getAuthorName } from "../model/orders.helpers";
import { useWhatsappDialogueModal } from "../hooks/use-whatsapp-dialogue-modal";

const SIZE = {
  sm: { box: "h-6 w-6", whatsapp: "h-[22px] w-[22px]" },
  md: { box: "h-8 w-8", whatsapp: "h-[30px] w-[30px]" },
};

/**
 * Who created the job, as an initials badge. A bot-created job shows the WhatsApp
 * mark instead, opening the dialogue; an author with a phone links to WhatsApp.
 */
export const OrderAuthor = ({ row, size = "md" }: { row: OrderRow; size?: "sm" | "md" }) => {
  const t = useT();
  const { openWhatsappDialogue } = useWhatsappDialogueModal();
  const authorName = getAuthorName(row);
  const author = row.author as { phoneNumber?: string; phone_number?: string } | undefined;
  const phoneNumber = (author?.phoneNumber || author?.phone_number)?.replace(/\s+/g, "");

  // Bot-created jobs are authored by the system user, so the WhatsApp
  // mark replaces its initials and opens the dialogue transcript.
  if (row.whatsapp_author_id) {
    return (
      <button
        type="button"
        title={t(Namespace.Orders, "whatsappDialogue.openDialogue")}
        aria-label={t(Namespace.Orders, "whatsappDialogue.openDialogue")}
        onClick={(e) => {
          e.stopPropagation();
          openWhatsappDialogue(row.rawJob);
        }}
        className={cn("flex shrink-0 items-center justify-center", SIZE[size].box)}
      >
        <IcWhatsapp className={cn("shrink-0", SIZE[size].whatsapp)} />
      </button>
    );
  }

  const badge = (
    <span
      title={authorName}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full text-xs font-semibold",
        SIZE[size].box,
        getAuthorBadgeClass(row.is_created_by_client),
      )}
    >
      {getAuthorInitials(row)}
    </span>
  );

  if (!phoneNumber) return badge;

  return (
    <a
      href={`https://wa.me/${reformatPhoneNumber(phoneNumber)}`}
      target="_blank"
      rel="noopener noreferrer"
      className="flex shrink-0 hover:opacity-80"
      aria-label={t(Namespace.Orders, "author.contactWhatsapp", { name: authorName })}
      onClick={(e) => e.stopPropagation()}
    >
      {badge}
    </a>
  );
};
